import { useState } from "react";

import { TinsmithMark } from "@tinsmith/brand";

import styles from "./Runners.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Modal } from "../../components/Modal";
import { ApiError } from "../../data/api/client";
import { enrollRunner } from "../../data/api/runners";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

type Phase = "form" | "key";

const enrollErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.code === "forbidden") {
      return "Enrolling a runner needs an admin or owner role.";
    }
    return err.message;
  }
  return "Couldn't enroll the runner. Try again.";
};

export type AddRunnerProps = {
  readonly onClose: () => void;
  // Parent refetches so the freshly enrolled (unconnected) runner appears.
  readonly onEnrolled: () => void;
};

export const AddRunner = ({ onClose, onEnrolled }: AddRunnerProps) => {
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<Phase>("form");
  const [copied, setCopied] = useState(false);
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runnerName = name.trim() || "new-runner";
  const host =
    (typeof window === "undefined" ? "" : window.location.host) ||
    "your-tinsmith-host";

  const enroll = async (): Promise<void> => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await enrollRunner({ name: runnerName });
      setToken(result.token);
      setPhase("key");
      onEnrolled();
    } catch (err) {
      setError(enrollErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = (): void => {
    void navigator.clipboard?.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal
      className={cx(styles.panel, "u-pop")}
      onClose={onClose}
      open
      title="Add a runner"
    >
      <div className={styles.modalHead}>
        <span className={styles.modalTitle}>Add a runner</span>
        <Button icon onClick={onClose} variant="ghost">
          <Icon.X size={15} />
        </Button>
      </div>

      {phase === "form" && (
        <div className={styles.addrBody}>
          <div className={styles.addrField}>
            <label>Runner name</label>
            <Input
              autoFocus
              className={styles.addrInput}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void enroll();
                }
              }}
              placeholder="e.g. sandbox-ci-box"
              value={name}
            />
          </div>
          <div className={styles.addrSummary}>
            <TinsmithMark size={18} />
            <span>
              Registers a self-hosted runner and mints a one-time key to install
              the <strong>tinsmith-runner</strong> agent on your own machine.
            </span>
          </div>
          {error ? <div className={styles.addrError}>{error}</div> : null}
          <div className={styles.actions}>
            <Button onClick={onClose}>Cancel</Button>
            <span className={styles.grow} />
            <Button
              disabled={submitting}
              onClick={() => void enroll()}
              variant="accent"
            >
              <Icon.ArrowRight size={13} />{" "}
              {submitting ? "Enrolling…" : "Enroll runner"}
            </Button>
          </div>
        </div>
      )}

      {phase === "key" && (
        <div className={styles.addrBody}>
          <div className={styles.addrKeyintro}>
            <div className={styles.rpanelLabel} style={{ marginBottom: 6 }}>
              Runner key
            </div>
            <p className={styles.rpanelHint}>
              Install the runtime on your machine and pass this key. It&apos;s
              shown once — copy it now.
            </p>
          </div>
          <div className={styles.addrKey}>
            <code>{token}</code>
            <Button onClick={copyKey} size="sm">
              {copied ? (
                <>
                  <Icon.Check size={13} /> Copied
                </>
              ) : (
                <>
                  <Icon.Copy size={13} /> Copy
                </>
              )}
            </Button>
          </div>
          <div className={styles.addrInstall}>
            <div className={styles.addrInstallLabel}>Then run</div>
            <pre className={styles.addrCode}>
              {`curl -fsSL https://${host}/install.sh | sh\ntinsmith-runner --name ${runnerName} \\\n  --server wss://${host}/api/v1/runners/connect \\\n  --key ${token.slice(0, 16)}…`}
            </pre>
          </div>
          <div className={styles.addrNote}>
            <TinsmithMark size={18} />
            <span>
              <strong>{runnerName}</strong> is enrolled. It stays{" "}
              <strong>Unconnected</strong> until it dials in — you can finish
              the install whenever.
            </span>
          </div>
          <div className={styles.actions}>
            <span className={styles.grow} />
            <Button onClick={onClose} variant="accent">
              <Icon.Check size={13} /> Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
